import { useAtomValue, useStore, type WritableAtom, type Atom, atom } from "jotai";
import { atomFamily, atomWithStorage, createJSONStorage } from "jotai/utils";
import type { LogicalRangeChangeEventHandler } from "lightweight-charts";
import { createContext, useContext, useEffect, useMemo, type MutableRefObject } from "react";
import { useBindableValue } from "@package/react/bindable";

import { sampleConfig, type TSample } from "../../../data/live-charts/config";
import { useChartDataContext } from "./data";

type ViewRange = { from: number; to: number; restored: boolean };
type ChartView = {
  range: WritableAtom<ViewRange, [ViewRange], void>;
  visible: Atom<number>;
  restore: (maxCandles: number) => void;
  windowSeconds: (candles: number) => number;
  setWindowSeconds: (p: { seconds: number; id: string; sampleTime?: TSample }) => void;
};

const windowsState = atomWithStorage<Record<string, ViewRange & { accessed: number }>>(
  "chart-windows",
  {},
  createJSONStorage(),
  { getOnInit: true },
);

const windows = atomFamily((p: { id: string; sampleTime?: TSample }) => {
  const key = `${p.sampleTime}-${p.id}`;
  return atom(
    (get) => {
      const state = get(windowsState);
      const stored = state[key];
      if (stored) return stored;
      const config = sampleConfig[p.sampleTime ?? "10s"];
      const initial = Math.floor(config.show / config.seconds);
      return { from: 0, to: initial, restored: false };
    },
    (get, set, update: ViewRange) => {
      const state = get(windowsState);
      set(windowsState, {
        ...state,
        [key]: { ...update, accessed: Date.now() },
      });
    },
  );
});

const ChartViewContext = createContext<ChartView | undefined>(undefined);

// Keep the loading overlay up for at least this long so a fast reset doesn't flash.
const MIN_RESET_LOADING_MS = 500;

export const ChartViewProvider = (p: {
  children: React.ReactNode;
  id: string;
  sampleTime?: TSample;
  resetTimescaleRef: MutableRefObject<boolean>;
  resetTimescaleAtRef: MutableRefObject<number>;
}) => {
  const { apiRef, setBackgroundLoading } = useChartDataContext();
  const api = useBindableValue(apiRef);
  const store = useStore();
  const { resetTimescaleRef, resetTimescaleAtRef } = p;

  const viewport = useMemo<ChartView>(() => {
    const range = windows({ id: p.id, sampleTime: p.sampleTime });
    store.set(range, { ...store.get(range), restored: false });
    return {
      range,
      visible: atom((get) => Math.max(10, get(range).to - get(range).from)),
      restore: (maxCandles) => {
        const stored = store.get(range);
        if (stored.restored) return;
        if (resetTimescaleRef.current) {
          resetTimescaleRef.current = false;
          // Mark restored now so a second restore() during the hold can't fall into the normal path below.
          store.set(range, { ...stored, restored: true });
          const remaining = Math.max(0, MIN_RESET_LOADING_MS - (Date.now() - resetTimescaleAtRef.current));
          // Changing sampleTime snaps the viewport to that sample's natural window (e.g. 1d -> ~1 month, 15m -> ~1 day) instead of carrying over the previous zoom or falling back to lightweight-charts' pixel-based default fit. This is the same window the atom defaults to (see the atomFamily above).
          const config = sampleConfig[p.sampleTime ?? "10s"];
          const naturalCandles = Math.floor(config.show / config.seconds);
          setTimeout(() => {
            setBackgroundLoading?.(false);
            apiRef
              .current()
              ?.timeScale()
              .setVisibleLogicalRange({ from: Math.max(0, maxCandles - naturalCandles), to: maxCandles });
          }, remaining);
          return;
        }
        const total = stored.to - stored.from;
        apiRef
          .current()
          ?.timeScale()
          .setVisibleLogicalRange({ from: Math.max(0, maxCandles - total), to: maxCandles });
        setTimeout(() => {
          store.set(range, { ...stored, restored: true });
        }, 10);
      },
      windowSeconds: (candles) => sampleConfig[p.sampleTime ?? "10s"].seconds * candles,
      setWindowSeconds: (a) => {
        const r = windows({ id: a.id, sampleTime: a.sampleTime });
        const candles = a.seconds / sampleConfig[a.sampleTime ?? "10s"].seconds;
        const to = store.get(viewport.range).to;
        const from = to - candles;
        store.set(r, { from, to, restored: false });
        if (a.sampleTime === p.sampleTime) {
          apiRef.current()?.timeScale().setVisibleLogicalRange({ from, to });
        }
      },
    };
  }, [p.sampleTime, p.id, apiRef, store, resetTimescaleRef, resetTimescaleAtRef, setBackgroundLoading]);

  useEffect(() => {
    if (!api) return;
    const handler: LogicalRangeChangeEventHandler = (r) => {
      if (!r) return;
      const stored = store.get(viewport.range);
      if (!stored.restored) return;

      store.set(viewport.range, { from: r.from, to: r.to, restored: true });
    };
    api.timeScale().subscribeVisibleLogicalRangeChange(handler);
    return () => api?.timeScale().unsubscribeVisibleLogicalRangeChange(handler);
  }, [api, store, viewport]);

  return <ChartViewContext.Provider value={viewport}>{p.children}</ChartViewContext.Provider>;
};

export const useChartView = () => {
  const context = useContext(ChartViewContext);
  if (!context) throw new Error("useChartView must be used within a ChartViewProvider");
  return context;
};

export const useChartViewRange = () => useAtomValue(useChartView().range);
